import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IoFilter } from "react-icons/io5";
import { RxReset } from "react-icons/rx";
import { useTranslation } from "react-i18next";
import AttToken from "../../helpers/attToken";
import NovoChamado from "../../service/NovoChamado";
import { RequesterTicketStatus, RequesterTicketsFilters } from "../../service/Tickets";
import AutocompleteWithApi from "../Autocomplete/AutocompleteWithApi";
import DatePickerFim from "../DatePicker/DatePickerFim";
import DatePickerInicio from "../DatePicker/DatePickerInicio";
import { Label } from "./style/SingleNotificationStyle";
import { trackEventMatomo } from "../../helpers/matomo";

type Option = { label: string; value: string };

type Props = {
  disabled: boolean;
  onApply: (filters: RequesterTicketsFilters | undefined) => void;
};

const ticketService = new NovoChamado();

const statusOptions: RequesterTicketStatus[] = [
  "pendente",
  "aceito",
  "em_andamento",
  "concluido",
  "recusado",
  "cancelado",
  "fechado",
  "fechadoCancelamento",
  "fechadoRecusa",
  "reaberto",
  "aprovado",
];

const toStartOfDay = (date: Date | number) => new Date(date).setHours(0, 0, 0, 0);
const toEndOfDay = (date: Date | number) => new Date(date).setHours(23, 59, 59, 999);

export default function RequesterFilters({ disabled, onApply }: Props) {
  const { t } = useTranslation("translation");
  const [expanded, setExpanded] = useState(false);
  const [openModels, setOpenModels] = useState(false);
  const [models, setModels] = useState<Option[]>([]);
  const [statuses, setStatuses] = useState<RequesterTicketStatus[]>([]);
  const [openedFrom, setOpenedFrom] = useState<Date | number | null>(null);
  const [openedTo, setOpenedTo] = useState<Date | number | null>(null);
  const [dueFrom, setDueFrom] = useState<Date | number | null>(null);
  const [dueTo, setDueTo] = useState<Date | number | null>(null);
  const [executedFrom, setExecutedFrom] = useState<Date | number | null>(null);
  const [executedTo, setExecutedTo] = useState<Date | number | null>(null);

  const { data: modelsData, isLoading, isFetching } = useQuery({
    queryKey: ["GET_REQUESTER_TICKET_MODELS", openModels],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token não encontrado");
      const response = await ticketService.getList(token);
      if (!response) throw new Error("Erro ao buscar modelos de chamado");
      return response;
    },
    enabled: openModels,
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const rangesAreComplete = [
    [openedFrom, openedTo],
    [dueFrom, dueTo],
    [executedFrom, executedTo],
  ].every(([from, to]) => (!from && !to) || (from && to));
  const selectedRanges = [
    openedFrom && openedTo,
    dueFrom && dueTo,
    executedFrom && executedTo,
  ].filter(Boolean).length;
  const canApplyRanges = rangesAreComplete && selectedRanges <= 1;

  const applyFilters = () => {
    trackEventMatomo('chamados do solicitante', 'click', 'botão', 'aplica filtros');
    const range = openedFrom && openedTo
      ? { key: "abertura" as const, de: toStartOfDay(openedFrom), ate: toEndOfDay(openedTo) }
      : dueFrom && dueTo
        ? { key: "previsao" as const, de: toStartOfDay(dueFrom), ate: toEndOfDay(dueTo) }
        : executedFrom && executedTo
          ? { key: "execucao" as const, de: toStartOfDay(executedFrom), ate: toEndOfDay(executedTo) }
          : undefined;
    const filters: RequesterTicketsFilters = {
      ...(models.length ? { modelos: models.map(model => model.value) } : {}),
      ...(statuses.length ? { status: statuses } : {}),
      ...(range ? { range } : {}),
    };

    onApply(Object.keys(filters).length ? filters : undefined);
  };

  const clearFilters = () => {
    trackEventMatomo('chamados do solicitante', 'click', 'botão', 'limpa filtros');
    setModels([]);
    setStatuses([]);
    setOpenedFrom(null);
    setOpenedTo(null);
    setDueFrom(null);
    setDueTo(null);
    setExecutedFrom(null);
    setExecutedTo(null);
    onApply(undefined);
  };

  return (
    <Accordion
      expanded={!disabled && expanded}
      disabled={disabled}
      onChange={(_event, isExpanded) => {
        trackEventMatomo('chamados do solicitante', 'click', 'filtros', isExpanded ? 'expande filtros' : 'recolhe filtros');
        setExpanded(isExpanded);
      }}
      sx={{ padding: 1, width: "100%" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ padding: 1 }}>
        <Typography textAlign="center" color="textDisabled" width="100%" variant="h5" fontWeight={700}>
          {t("table.title")}
        </Typography>
      </AccordionSummary>
      <Divider variant="middle" flexItem orientation="horizontal" />
      <Stack sx={{ padding: "15px" }} direction="column" justifyContent="center" spacing={2}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <Button disabled={disabled || !canApplyRanges} endIcon={<IoFilter />} className="button is-success" variant="contained" onClick={applyFilters}>
            {t("filter")}
          </Button>
          <Button disabled={disabled} endIcon={<RxReset />} className="button is-danger" variant="contained" onClick={clearFilters}>
            {t("notifications.myTickets.cleanFilter")}
          </Button>
        </Stack>

        <Label className="filtersTitle">{t("notifications.ticketsFilters.byTickets")}</Label>
        <Divider />
        <AutocompleteWithApi
          onChange={values => setModels((values ?? []) as Option[])}
          selectedValues={models}
          setOpen={setOpenModels}
          options={modelsData?.map((model: { id: string; name: string }) => ({ label: model.name, value: model.id })) ?? []}
          loading={isLoading || isFetching}
          textoVazio={t("notifications.ticketsFilters.noMatchingTickets")}
          textoLoading={t("notifications.ticketsFilters.searchingTickets")}
          textLabel={t("notifications.ticketsFilters.selectOneOrMoreTicketModels2")}
        />

        <Label className="filtersTitle">{t("notifications.ticketsFilters.byStatus")}</Label>
        <Divider variant="middle" />
        <FormControl fullWidth>
          <InputLabel id="requester-status-label">{t("status")}</InputLabel>
          <Select
            multiple
            labelId="requester-status-label"
            value={statuses}
            label={t("status")}
            input={<OutlinedInput label={t("status")} />}
            onChange={event => {
              trackEventMatomo('chamados do solicitante', 'click', 'select', 'seleciona status');
              setStatuses(typeof event.target.value === "string" ? event.target.value.split(",") as RequesterTicketStatus[] : event.target.value as RequesterTicketStatus[]);
            }}
          >
            {statusOptions.map(status => (
              <MenuItem key={status} value={status}>{t(`notifications.requester.status.${status}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Label className="filtersTitle">{t("notifications.myTickets.perDate")}</Label>
        <Divider variant="middle" />
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} flexWrap="wrap">
          <Stack direction="column" alignItems="center" justifyContent='space-between' spacing={2}>
            <Label>{t("filterLabels.cadastro")}</Label>
            <DatePickerInicio maxDate={openedTo} inicio={openedFrom} setInicio={setOpenedFrom} label={t("filterLabels.inicio")} />
            <DatePickerFim inicio={openedFrom} fim={openedTo} setFim={setOpenedTo} label={t("Reports.columns.end")} />
          </Stack>
          <Stack direction="column" alignItems="center" spacing={2}>
            <Label>{t("filterLabels.limite")}</Label>
            <DatePickerInicio maxDate={dueTo} inicio={dueFrom} setInicio={setDueFrom} label={t("filterLabels.inicio")} />
            <DatePickerFim inicio={dueFrom} fim={dueTo} setFim={setDueTo} label={t("Reports.columns.end")} />
          </Stack>
          <Stack direction="column" alignItems="center" spacing={2}>
            <Label>{t("notifications.requester.executionDate")}</Label>
            <DatePickerInicio maxDate={executedTo} inicio={executedFrom} setInicio={setExecutedFrom} label={t("filterLabels.inicio")} />
            <DatePickerFim inicio={executedFrom} fim={executedTo} setFim={setExecutedTo} label={t("Reports.columns.end")} />
          </Stack>
        </Stack>
        {!rangesAreComplete && (
          <Typography color="textDisabled" textAlign="center">{t("notifications.requester.completeDateRange")}</Typography>
        )}
      </Stack>
    </Accordion>
  );
}
